import { css_color_to_hex } from '@/uptk/core/theme/theme_css_variables';
import type {
  ThemeMode,
  ThemeShapeConfiguration,
  ThemeTokenKey,
  ThemeTypographyConfiguration,
} from '@/types/theme_configuration';

type RgbColor = {
  r: number;
  g: number;
  b: number;
};

type HslColor = {
  h: number;
  s: number;
  l: number;
};

export type DesignImportResult = {
  colors: DesignImportColors;
  typography: Required<ThemeTypographyConfiguration>;
  shape: Required<ThemeShapeConfiguration>;
  detectedTitle: string | null;
  notes: string[];
};

export type DesignImportColors = {
  seed: string;
  primary: string;
  secondary: string;
  tertiary: string;
  neutral: string;
  accent: string;
  background: string;
  surface: string;
  surfaceMuted: string;
  border: string;
  foreground: string;
  foregroundMuted: string;
  primaryForeground: string;
  success: string;
  warning: string;
  danger: string;
};

type FontMatch = {
  fontFamily: string;
  index: number;
};

export type StitchPaletteFamilyKey = 'primary' | 'secondary' | 'tertiary' | 'neutral';

export type StitchPaletteScaleStep = {
  grade: string;
  color: string;
};

export type StitchPaletteFamily = {
  key: StitchPaletteFamilyKey;
  label: string;
  baseColor: string;
  scale: StitchPaletteScaleStep[];
};

const hex_color_expression = /#(?:[0-9a-f]{3}|[0-9a-f]{6})\b/i;
const markdown_heading_expression = /^\s*#\s+(.+)$/m;

export const stitch_theme_font_options = ['Inter', 'Lexend', 'Manrope', 'Figtree'] as const;
export const stitch_theme_corner_radius_options = [0, 8, 12, 24] as const;

const fallback_light_seed = '#2563eb';
const fallback_dark_seed = '#3b82f6';
const stitch_scale_grades = ['100', '200', '300', '400', '500', '600', '700', '800'] as const;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const normalize_hue = (value: number) => {
  const bounded = value % 360;
  return bounded < 0 ? bounded + 360 : bounded;
};

const parse_hex_color = (value: string): RgbColor => {
  const normalized = css_color_to_hex(value, fallback_light_seed).replace('#', '');
  const expanded = normalized.length === 3
    ? normalized
        .split('')
        .map((character) => `${character}${character}`)
        .join('')
    : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

const rgb_to_hex = ({ r, g, b }: RgbColor) => {
  const to_hex = (channel: number) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0');
  return `#${to_hex(r)}${to_hex(g)}${to_hex(b)}`;
};

const rgb_to_hsl = ({ r, g, b }: RgbColor): HslColor => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return {
      h: 0,
      s: 0,
      l: lightness * 100,
    };
  }

  const difference = max - min;
  const saturation =
    lightness > 0.5
      ? difference / (2 - max - min)
      : difference / (max + min);

  let hue = 0;
  switch (max) {
    case red:
      hue = (green - blue) / difference + (green < blue ? 6 : 0);
      break;
    case green:
      hue = (blue - red) / difference + 2;
      break;
    default:
      hue = (red - green) / difference + 4;
      break;
  }

  return {
    h: normalize_hue(hue * 60),
    s: saturation * 100,
    l: lightness * 100,
  };
};

const hsl_to_rgb = ({ h, s, l }: HslColor): RgbColor => {
  const saturation = clamp(s / 100, 0, 1);
  const lightness = clamp(l / 100, 0, 1);
  const hue = normalize_hue(h) / 360;

  if (saturation === 0) {
    const channel = Math.round(lightness * 255);
    return { r: channel, g: channel, b: channel };
  }

  const hue_to_rgb = (p: number, q: number, value: number) => {
    let adjusted = value;
    if (adjusted < 0) adjusted += 1;
    if (adjusted > 1) adjusted -= 1;
    if (adjusted < 1 / 6) return p + (q - p) * 6 * adjusted;
    if (adjusted < 1 / 2) return q;
    if (adjusted < 2 / 3) return p + (q - p) * (2 / 3 - adjusted) * 6;
    return p;
  };

  const q = lightness < 0.5
    ? lightness * (1 + saturation)
    : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return {
    r: Math.round(hue_to_rgb(p, q, hue + 1 / 3) * 255),
    g: Math.round(hue_to_rgb(p, q, hue) * 255),
    b: Math.round(hue_to_rgb(p, q, hue - 1 / 3) * 255),
  };
};

const mix_colors = (source: string, target: string, ratio: number) => {
  const sourceRgb = parse_hex_color(source);
  const targetRgb = parse_hex_color(target);
  const weight = clamp(ratio, 0, 1);
  return rgb_to_hex({
    r: sourceRgb.r * (1 - weight) + targetRgb.r * weight,
    g: sourceRgb.g * (1 - weight) + targetRgb.g * weight,
    b: sourceRgb.b * (1 - weight) + targetRgb.b * weight,
  });
};

const adjust_hsl = (
  color: string,
  overrides: Partial<HslColor>
) => {
  const current = rgb_to_hsl(parse_hex_color(color));
  return rgb_to_hex(
    hsl_to_rgb({
      h: overrides.h ?? current.h,
      s: clamp(overrides.s ?? current.s, 0, 100),
      l: clamp(overrides.l ?? current.l, 0, 100),
    })
  );
};

const create_neutral_from_seed = (seedColor: string, saturation: number, lightness: number) => {
  const { h } = rgb_to_hsl(parse_hex_color(seedColor));
  return rgb_to_hex(hsl_to_rgb({ h, s: saturation, l: lightness }));
};

const relative_luminance = (color: string) => {
  const { r, g, b } = parse_hex_color(color);
  const linearize = (channel: number) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };

  return (
    0.2126 * linearize(r) +
    0.7152 * linearize(g) +
    0.0722 * linearize(b)
  );
};

const contrast_ratio = (left: string, right: string) => {
  const leftLuminance = relative_luminance(left);
  const rightLuminance = relative_luminance(right);
  const [lighter, darker] =
    leftLuminance > rightLuminance
      ? [leftLuminance, rightLuminance]
      : [rightLuminance, leftLuminance];
  return (lighter + 0.05) / (darker + 0.05);
};

const choose_readable_foreground = (background: string) => {
  const white = '#ffffff';
  const ink = '#0f172a';
  return contrast_ratio(background, white) >= contrast_ratio(background, ink) ? white : ink;
};

const is_dark_color = (color: string) => relative_luminance(color) < 0.35;

const is_light_color = (color: string) => relative_luminance(color) > 0.6;

const normalize_font_family = (value: string | null | undefined, fallback: string) => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
};

const extract_title = (markdown: string) => {
  const match = markdown.match(markdown_heading_expression);
  return match ? match[1].trim() : null;
};

const match_color = (markdown: string, expressions: RegExp[]) => {
  for (const expression of expressions) {
    const match = markdown.match(expression);
    if (match?.[1]) {
      return css_color_to_hex(match[1], fallback_light_seed);
    }
  }
  return null;
};

const match_first_hex_color = (markdown: string) => {
  const match = markdown.match(hex_color_expression);
  return match ? css_color_to_hex(match[0], fallback_light_seed) : null;
};

const match_fonts = (markdown: string) => {
  const matches: FontMatch[] = [];
  stitch_theme_font_options.forEach((fontFamily) => {
    const expression = new RegExp(fontFamily, 'ig');
    let match = expression.exec(markdown);
    while (match) {
      matches.push({ fontFamily, index: match.index });
      match = expression.exec(markdown);
    }
  });
  return matches.sort((left, right) => left.index - right.index);
};

const match_font_near_keywords = (markdown: string, keywords: string[]) => {
  const lowerMarkdown = markdown.toLowerCase();
  const allFonts = match_fonts(markdown);
  let bestMatch: FontMatch | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  allFonts.forEach((fontMatch) => {
    const searchWindowStart = Math.max(0, fontMatch.index - 80);
    const searchWindowEnd = Math.min(lowerMarkdown.length, fontMatch.index + 120);
    const searchWindow = lowerMarkdown.slice(searchWindowStart, searchWindowEnd);

    if (!keywords.some((keyword) => searchWindow.includes(keyword))) {
      return;
    }

    const distance = Math.min(
      ...keywords
        .map((keyword) => searchWindow.indexOf(keyword))
        .filter((index) => index >= 0)
        .map((index) => Math.abs(index - (fontMatch.index - searchWindowStart)))
    );

    if (distance < bestDistance) {
      bestMatch = fontMatch;
      bestDistance = distance;
    }
  });

  return bestMatch?.fontFamily ?? null;
};

const parse_length_to_px = (value: string) => {
  const trimmed = value.trim().toLowerCase();
  if (trimmed.endsWith('rem')) {
    return Math.round(parseFloat(trimmed.replace('rem', '')) * 16);
  }
  if (trimmed.endsWith('px')) {
    return Math.round(parseFloat(trimmed.replace('px', '')));
  }
  return Math.round(parseFloat(trimmed));
};

const match_corner_radius = (markdown: string) => {
  const expressions = [
    /(\d+(?:\.\d+)?(?:rem|px))[^.\n\r]{0,40}corner radius/i,
    /corner radius[^.\n\r]{0,40}(\d+(?:\.\d+)?(?:rem|px))/i,
    /soft-edge[^.\n\r]{0,30}\((\w+)\s+radius\)/i,
  ];

  for (const expression of expressions) {
    const match = markdown.match(expression);
    if (!match?.[1]) {
      continue;
    }

    const value = match[1].toLowerCase();
    if (value === 'lg') {
      return 12;
    }
    if (value === 'md') {
      return 8;
    }
    if (value === 'sm') {
      return 4;
    }
    return clamp(parse_length_to_px(value), 0, 32);
  }

  return null;
};

const rebuild_palette_tokens = (tokens: Omit<Record<ThemeTokenKey, string>, `color${number}`>) => {
  return {
    ...tokens,
    color1: tokens.primary,
    color2: tokens.foreground,
    color3: tokens.accent,
    color4: tokens.background,
    color5: tokens.surface,
    color6: tokens.surfaceMuted,
    color7: tokens.foregroundMuted,
    color8: tokens.border,
    color9: tokens.success,
    color10: tokens.danger,
  };
};

export const build_design_import_colors = (
  tokens: Partial<Record<ThemeTokenKey, string>>,
  seedColor: string,
  mode: ThemeMode
): DesignImportColors => {
  const generatedTokens = build_theme_tokens_from_seed_color(seedColor, mode);
  const primary = css_color_to_hex(tokens.primary ?? generatedTokens.primary, generatedTokens.primary);
  const tertiary = css_color_to_hex(tokens.tertiary ?? generatedTokens.tertiary, generatedTokens.tertiary);

  return {
    seed: css_color_to_hex(seedColor, mode === 'dark' ? fallback_dark_seed : fallback_light_seed),
    primary,
    secondary: css_color_to_hex(tokens.secondary ?? generatedTokens.secondary, generatedTokens.secondary),
    tertiary,
    neutral: css_color_to_hex(tokens.neutral ?? generatedTokens.neutral, generatedTokens.neutral),
    accent: css_color_to_hex(tokens.accent ?? tertiary, tertiary),
    background: css_color_to_hex(tokens.background ?? generatedTokens.background, generatedTokens.background),
    surface: css_color_to_hex(tokens.surface ?? generatedTokens.surface, generatedTokens.surface),
    surfaceMuted: css_color_to_hex(tokens.surfaceMuted ?? generatedTokens.surfaceMuted, generatedTokens.surfaceMuted),
    border: css_color_to_hex(tokens.border ?? generatedTokens.border, generatedTokens.border),
    foreground: css_color_to_hex(tokens.foreground ?? generatedTokens.foreground, generatedTokens.foreground),
    foregroundMuted: css_color_to_hex(tokens.foregroundMuted ?? generatedTokens.foregroundMuted, generatedTokens.foregroundMuted),
    primaryForeground: css_color_to_hex(
      tokens.primaryForeground ?? choose_readable_foreground(primary),
      choose_readable_foreground(primary)
    ),
    success: css_color_to_hex(tokens.success ?? generatedTokens.success, generatedTokens.success),
    warning: css_color_to_hex(tokens.warning ?? generatedTokens.warning, generatedTokens.warning),
    danger: css_color_to_hex(tokens.danger ?? generatedTokens.danger, generatedTokens.danger),
  };
};

export const build_theme_tokens_from_seed_color = (
  seedColor: string,
  mode: ThemeMode
): Record<ThemeTokenKey, string> => {
  const normalizedSeed = css_color_to_hex(
    seedColor,
    mode === 'dark' ? fallback_dark_seed : fallback_light_seed
  );
  const baseSeed = mode === 'dark'
    ? adjust_hsl(normalizedSeed, { l: clamp(rgb_to_hsl(parse_hex_color(normalizedSeed)).l + 8, 42, 70) })
    : normalizedSeed;
  const baseSeedHsl = rgb_to_hsl(parse_hex_color(baseSeed));
  const secondary = rgb_to_hex(
    hsl_to_rgb({
      h: baseSeedHsl.h,
      s: clamp(baseSeedHsl.s * 0.35, 10, 34),
      l: mode === 'dark' ? 60 : 46,
    })
  );
  const tertiary = adjust_hsl(baseSeed, {
    h: baseSeedHsl.h + 28,
    l: mode === 'dark' ? 68 : 50,
    s: clamp(baseSeedHsl.s + 6, 42, 86),
  });
  const neutral = create_neutral_from_seed(
    baseSeed,
    mode === 'dark' ? 8 : 10,
    mode === 'dark' ? 56 : 52
  );

  if (mode === 'dark') {
    return rebuild_palette_tokens({
      primary: baseSeed,
      secondary,
      tertiary,
      neutral,
      primaryForeground: choose_readable_foreground(baseSeed),
      background: create_neutral_from_seed(baseSeed, 24, 8),
      surface: create_neutral_from_seed(baseSeed, 22, 14),
      surfaceMuted: create_neutral_from_seed(baseSeed, 20, 18),
      foreground: '#f8fafc',
      foregroundMuted: create_neutral_from_seed(baseSeed, 12, 80),
      border: create_neutral_from_seed(baseSeed, 14, 28),
      accent: tertiary,
      success: '#22c55e',
      warning: '#f59e0b',
      danger: '#f87171',
      // UPT-1968 : l'information ne suit pas la marque — un theme rouge
      // rendait ses informations indiscernables d'une erreur.
      info: '#38bdf8',
      studioBackground: '#070C12',
    });
  }

  return rebuild_palette_tokens({
    primary: baseSeed,
    secondary,
    tertiary,
    neutral,
    primaryForeground: choose_readable_foreground(baseSeed),
    background: create_neutral_from_seed(baseSeed, 14, 98),
    surface: '#ffffff',
    surfaceMuted: mix_colors(baseSeed, '#ffffff', 0.88),
    foreground: create_neutral_from_seed(baseSeed, 18, 15),
    foregroundMuted: create_neutral_from_seed(baseSeed, 10, 40),
    border: create_neutral_from_seed(baseSeed, 12, 86),
    accent: tertiary,
    success: '#16a34a',
    warning: '#d97706',
    danger: '#dc2626',
    info: '#0284c7',
    studioBackground: '#EFF7FB',
  });
};

const build_stitch_tone_scale = (
  baseColor: string,
  familyKey: StitchPaletteFamilyKey
): StitchPaletteScaleStep[] => {
  const normalizedBase = css_color_to_hex(baseColor, fallback_light_seed);
  const baseHsl = rgb_to_hsl(parse_hex_color(normalizedBase));
  const familySaturation = {
    primary: clamp(baseHsl.s, 28, 92),
    secondary: clamp(baseHsl.s, 10, 34),
    tertiary: clamp(baseHsl.s, 16, 76),
    neutral: clamp(baseHsl.s, 2, 14),
  }[familyKey];

  const toneLightness = [
    clamp(baseHsl.l + 36, 82, 98),
    clamp(baseHsl.l + 28, 72, 94),
    clamp(baseHsl.l + 18, 62, 88),
    clamp(baseHsl.l + 8, 52, 80),
    clamp(baseHsl.l, 44, 72),
    clamp(baseHsl.l - 10, 32, 62),
    clamp(baseHsl.l - 20, 22, 52),
    clamp(baseHsl.l - 30, 12, 42),
  ];

  return stitch_scale_grades.map((grade, index) => ({
    grade,
    color: rgb_to_hex(
      hsl_to_rgb({
        h: baseHsl.h,
        s: familySaturation,
        l: toneLightness[index],
      })
    ),
  }));
};

export const resolve_stitch_palette_families = (
  primaryColor: string,
  secondaryColor: string,
  tertiaryColor: string,
  neutralColor: string,
  mode: ThemeMode
): StitchPaletteFamily[] => {
  const normalizedPrimary = css_color_to_hex(
    primaryColor,
    mode === 'dark' ? fallback_dark_seed : fallback_light_seed
  );
  const primaryHsl = rgb_to_hsl(parse_hex_color(normalizedPrimary));
  const derivedSecondary = rgb_to_hex(
    hsl_to_rgb({
      h: primaryHsl.h,
      s: clamp(primaryHsl.s * 0.35, 10, 28),
      l: mode === 'dark' ? 60 : 46,
    })
  );
  const derivedTertiary = adjust_hsl(normalizedPrimary, {
    h: primaryHsl.h + 28,
    l: mode === 'dark' ? 68 : 50,
    s: clamp(primaryHsl.s + 6, 42, 86),
  });
  const derivedNeutral = create_neutral_from_seed(
    normalizedPrimary,
    mode === 'dark' ? 8 : 10,
    mode === 'dark' ? 56 : 52
  );
  const normalizedSecondary = css_color_to_hex(secondaryColor, derivedSecondary);
  const normalizedTertiary = css_color_to_hex(tertiaryColor, derivedTertiary);
  const normalizedNeutral = css_color_to_hex(neutralColor, derivedNeutral);

  const families: Array<{ key: StitchPaletteFamilyKey; label: string; baseColor: string }> = [
    { key: 'primary', label: 'Primary', baseColor: normalizedPrimary },
    { key: 'secondary', label: 'Secondary', baseColor: normalizedSecondary },
    { key: 'tertiary', label: 'Tertiary', baseColor: normalizedTertiary },
    { key: 'neutral', label: 'Neutral', baseColor: normalizedNeutral },
  ];

  return families.map((family) => ({
    ...family,
    scale: build_stitch_tone_scale(family.baseColor, family.key),
  }));
};

const resolve_should_apply_imported_color = (
  tokenKey: ThemeTokenKey,
  value: string,
  mode: ThemeMode
) => {
  if (mode === 'light') {
    return true;
  }

  if (
    tokenKey === 'primary' ||
    tokenKey === 'secondary' ||
    tokenKey === 'tertiary' ||
    tokenKey === 'neutral' ||
    tokenKey === 'accent' ||
    tokenKey === 'success' ||
    tokenKey === 'warning' ||
    tokenKey === 'danger' ||
    tokenKey === 'color1' ||
    tokenKey === 'color3' ||
    tokenKey === 'color9' ||
    tokenKey === 'color10'
  ) {
    return true;
  }

  if (
    tokenKey === 'foreground' ||
    tokenKey === 'foregroundMuted' ||
    tokenKey === 'primaryForeground' ||
    tokenKey === 'color2' ||
    tokenKey === 'color7'
  ) {
    return is_light_color(value);
  }

  return is_dark_color(value);
};

const build_explicit_import_tokens = (markdown: string, mode: ThemeMode) => {
  const tokens: Partial<Record<ThemeTokenKey, string>> = {};
  const explicitPrimary = match_color(markdown, [
    /primary(?:\s+color|\b)[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /seed color[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /brand[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitAccent = match_color(markdown, [
    /accent[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /secondary_container[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /primary_dim[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /blue[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitSecondary = match_color(markdown, [
    /secondary(?:\s+color|\b)[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /supporting[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitTertiary = match_color(markdown, [
    /tertiary(?:\s+color|\b)[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /accent palette[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitNeutral = match_color(markdown, [
    /neutral(?:\s+color|\b)[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /gray[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitBackground = match_color(markdown, [
    /base layer[^#\n\r]{0,60}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /surface\b[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitSurface = match_color(markdown, [
    /surface_container_lowest[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /interactive objects[^#\n\r]{0,80}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitSurfaceMuted = match_color(markdown, [
    /surface_container_low[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /primary workspaces[^#\n\r]{0,80}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitBorder = match_color(markdown, [
    /surface_container_highest[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /outline_variant[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /ghost border[^#\n\r]{0,80}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitForeground = match_color(markdown, [
    /on_surface[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /text[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitSuccess = match_color(markdown, [
    /success[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /green[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitWarning = match_color(markdown, [
    /warning[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /yellow[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /orange[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);
  const explicitDanger = match_color(markdown, [
    /danger[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /error[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /red[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]);

  const explicitValues: Partial<Record<ThemeTokenKey, string>> = {
    ...(explicitPrimary ? { primary: explicitPrimary } : {}),
    ...(explicitSecondary ? { secondary: explicitSecondary } : {}),
    ...(explicitTertiary || explicitAccent ? { tertiary: explicitTertiary ?? explicitAccent ?? '' } : {}),
    ...(explicitNeutral ? { neutral: explicitNeutral } : {}),
    ...(explicitAccent ? { accent: explicitAccent } : {}),
    ...(explicitBackground ? { background: explicitBackground } : {}),
    ...(explicitSurface ? { surface: explicitSurface } : {}),
    ...(explicitSurfaceMuted ? { surfaceMuted: explicitSurfaceMuted } : {}),
    ...(explicitBorder ? { border: explicitBorder } : {}),
    ...(explicitForeground ? { foreground: explicitForeground } : {}),
    ...(explicitSuccess ? { success: explicitSuccess } : {}),
    ...(explicitWarning ? { warning: explicitWarning } : {}),
    ...(explicitDanger ? { danger: explicitDanger } : {}),
  };

  (Object.entries(explicitValues) as Array<[ThemeTokenKey, string]>).forEach(([tokenKey, value]) => {
    if (resolve_should_apply_imported_color(tokenKey, value, mode)) {
      tokens[tokenKey] = value;
    }
  });

  return tokens;
};

const resolve_imported_typography = (markdown: string): Required<ThemeTypographyConfiguration> => {
  const displayFontFamily =
    match_font_near_keywords(markdown, ['display', 'headline', 'command center']) ??
    match_fonts(markdown)[0]?.fontFamily ??
    'Lexend';
  const bodyFontFamily =
    match_font_near_keywords(markdown, ['body', 'workhorse']) ??
    match_fonts(markdown).find((match) => match.fontFamily !== displayFontFamily)?.fontFamily ??
    'Inter';
  const labelFontFamily =
    match_font_near_keywords(markdown, ['label']) ??
    bodyFontFamily;

  return {
    displayFontFamily,
    bodyFontFamily,
    labelFontFamily,
  };
};

const resolve_imported_shape = (markdown: string): Required<ThemeShapeConfiguration> => ({
  cornerRadius: match_corner_radius(markdown) ?? 8,
});

export const import_theme_mode_configuration_from_design_markdown = (
  markdown: string,
  mode: ThemeMode
): DesignImportResult => {
  const detectedTitle = extract_title(markdown);
  const detectedSeedColor = match_color(markdown, [
    /primary(?:\s+color|\b)[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /seed color[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
    /brand[^#\n\r]{0,40}(#(?:[0-9a-f]{3}|[0-9a-f]{6}))/i,
  ]) ??
    match_first_hex_color(markdown) ??
    (mode === 'dark' ? fallback_dark_seed : fallback_light_seed);
  const generatedTokens = build_theme_tokens_from_seed_color(detectedSeedColor, mode);
  const importedTokens = build_explicit_import_tokens(markdown, mode);
  const semanticTokens = {
    ...generatedTokens,
    ...importedTokens,
  };
  const primaryForeground = choose_readable_foreground(semanticTokens.primary);
  const resolvedTokens = rebuild_palette_tokens({
    primary: semanticTokens.primary,
    secondary: semanticTokens.secondary,
    tertiary: semanticTokens.tertiary,
    neutral: semanticTokens.neutral,
    primaryForeground,
    background: semanticTokens.background,
    surface: semanticTokens.surface,
    surfaceMuted: semanticTokens.surfaceMuted,
    foreground: semanticTokens.foreground,
    foregroundMuted: semanticTokens.foregroundMuted,
    border: semanticTokens.border,
    accent: semanticTokens.accent,
    success: semanticTokens.success,
    warning: semanticTokens.warning,
    danger: semanticTokens.danger,
    info: semanticTokens.info,
    studioBackground: semanticTokens.studioBackground,
  });
  const typography = resolve_imported_typography(markdown);
  const shape = resolve_imported_shape(markdown);
  const colors = build_design_import_colors(resolvedTokens, detectedSeedColor, mode);
  const notes = [
    `Seed color ${detectedSeedColor} mapped to ${mode} mode.`,
    `Typography: ${typography.displayFontFamily} / ${typography.bodyFontFamily}.`,
    `Corner radius: ${shape.cornerRadius}px.`,
  ];

  if (detectedTitle) {
    notes.unshift(`Detected design title: ${detectedTitle}.`);
  }

  return {
    colors,
    typography,
    shape,
    detectedTitle,
    notes,
  };
};

/**
 * UPT-1968 — ne soumettre a l'analyse que la section du mode demande.
 *
 * Un brief decrit souvent les DEUX themes, l'un sous l'autre — le DESIGN.md que
 * nous produisons le fait toujours. Envoye entier, il laisse l'analyse choisir
 * elle-meme laquelle lire : demander le clair pouvait rendre les couleurs du
 * sombre. On retire donc la section de l'AUTRE mode, et seulement elle : tout
 * ce qui est commun — palette, typographie, intentions — reste, sans quoi on
 * perdrait la moitie du brief pour corriger un aiguillage.
 *
 * Un brief sans ces sections est rendu tel quel : rien a departager.
 */
export const strip_other_mode_section = (markdown: string, mode: ThemeMode): string => {
  const other = mode === 'light' ? 'DARK' : 'LIGHT';
  // `\\s` et non `\s` : dans un litteral gabarit, `\s` perd son antislash et
  // deviendrait la lettre « s » — le motif ne reconnaitrait plus rien.
  const otherHeading = new RegExp(`^#{1,6}\\s*${other}\\s+Theme\\s+Instructions\\s*$`, 'im');
  const start = markdown.search(otherHeading);
  if (start < 0) {
    return markdown;
  }
  // La section court jusqu'au titre suivant de niveau 1 a 3, ou la fin du
  // texte. On cherche APRES la ligne de titre : chercher depuis le titre
  // lui-meme le retrouverait aussitot. Les sous-titres de niveau 4 (`####
  // DARK Guidance`) ne coupent pas — ils appartiennent a la section.
  const afterHeadingLine = markdown.indexOf('\n', start);
  if (afterHeadingLine < 0) {
    return markdown.slice(0, start);
  }
  const nextHeading = markdown.slice(afterHeadingLine).search(/^#{1,3}\s+\S/m);
  const end = nextHeading < 0 ? markdown.length : afterHeadingLine + nextHeading;
  return `${markdown.slice(0, start)}${markdown.slice(end)}`;
};

export const resolve_theme_typography_defaults = (
  typography?: ThemeTypographyConfiguration | null
): Required<ThemeTypographyConfiguration> => ({
  displayFontFamily: normalize_font_family(typography?.displayFontFamily, 'Lexend'),
  bodyFontFamily: normalize_font_family(typography?.bodyFontFamily, 'Inter'),
  labelFontFamily: normalize_font_family(typography?.labelFontFamily, typography?.bodyFontFamily ?? 'Inter'),
});

/**
 * UPT-1968 — un rayon relu en chaîne reste un rayon.
 *
 * Le thème est du JSON venu du serveur : rien n'y garantit le type annoncé, et
 * un `"24"` s'y glissait. La lecture n'acceptant qu'un nombre, elle le jetait
 * pour retomber sur le défaut — le réglage de l'utilisateur disparaissait à la
 * relecture sans qu'aucune erreur ne le signale. On accepte donc toute valeur
 * numériquement lisible, et on rejette le reste comme avant.
 */
const to_finite_number = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const resolve_theme_shape_defaults = (
  shape?: ThemeShapeConfiguration | null
): Required<ThemeShapeConfiguration> => ({
  cornerRadius: clamp(to_finite_number(shape?.cornerRadius) ?? 8, 0, 32),
});

/**
 * UPT-1955 : l'echelle de rayons vit desormais dans theme_shape.ts (source
 * unique partagee par l'apercu du configurateur et le runtime du portail).
 * Re-export conserve pour les appelants historiques.
 */
export { resolve_theme_radius_scale } from '@/uptk/core/theme/theme_shape';
